import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './AdminAnalytics.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const API_VERSION = process.env.REACT_APP_API_VERSION || 'v1';

const AdminAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30days');

  const fetchAnalytics = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${API_URL}/api/${API_VERSION}/admin/analytics?range=${timeRange}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setAnalytics(response.data.data || getMockAnalytics());
    } catch (error) {
      console.error('Error fetching analytics:', error);
      // Use mock data if API fails
      setAnalytics(getMockAnalytics());
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const getMockAnalytics = () => ({
    overview: {
      totalRevenue: 125000,
      revenueGrowth: 15.3,
      activeSubscriptions: 45,
      subscriptionGrowth: 8.2,
      totalUsers: 234,
      userGrowth: 12.5,
      avgSessionDuration: '8m 32s',
      sessionGrowth: -2.1
    },
    userActivity: {
      dailyActiveUsers: [120, 135, 142, 138, 155, 148, 162],
      weeklyActiveUsers: [450, 478, 492, 485, 510, 498, 525],
      labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    },
    revenue: {
      monthly: [8500, 9200, 10100, 11500, 12800, 13500],
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun']
    },
    topOrganizations: [
      { name: 'Tech Corp', users: 45, revenue: 25000, growth: 18 },
      { name: 'Innovation Labs', users: 38, revenue: 22000, growth: 12 },
      { name: 'Digital Solutions', users: 32, revenue: 18500, growth: 25 },
      { name: 'Cloud Systems', users: 28, revenue: 16000, growth: 8 },
      { name: 'Data Analytics Inc', users: 25, revenue: 14500, growth: 15 }
    ],
    featureUsage: [
      { feature: 'Project Management', usage: 92, trend: 'up' },
      { feature: 'Time Tracking', usage: 78, trend: 'up' },
      { feature: 'Resource Planning', usage: 65, trend: 'stable' },
      { feature: 'Reporting', usage: 58, trend: 'up' },
      { feature: 'Integrations', usage: 42, trend: 'down' }
    ]
  });

  if (loading) {
    return (
      <div className="admin-analytics">
        <div className="loading">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-analytics">
      <div className="page-header">
        <h1>Analytics Dashboard</h1>
        <div className="time-range-selector">
          <button 
            className={timeRange === '7days' ? 'active' : ''}
            onClick={() => setTimeRange('7days')}
          >
            7 Days
          </button>
          <button 
            className={timeRange === '30days' ? 'active' : ''}
            onClick={() => setTimeRange('30days')}
          >
            30 Days
          </button>
          <button 
            className={timeRange === '90days' ? 'active' : ''}
            onClick={() => setTimeRange('90days')}
          >
            90 Days
          </button>
          <button 
            className={timeRange === '1year' ? 'active' : ''}
            onClick={() => setTimeRange('1year')}
          >
            1 Year
          </button>
        </div>
      </div>

      {/* Overview Metrics */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-icon revenue">
            <i className="fas fa-dollar-sign"></i>
          </div>
          <div className="metric-content">
            <h3>Total Revenue</h3>
            <div className="metric-value">${analytics.overview.totalRevenue.toLocaleString()}</div>
            <div className={`metric-change ${analytics.overview.revenueGrowth >= 0 ? 'positive' : 'negative'}`}>
              <i className={`fas fa-arrow-${analytics.overview.revenueGrowth >= 0 ? 'up' : 'down'}`}></i>
              {Math.abs(analytics.overview.revenueGrowth)}% vs last period
            </div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon subscriptions">
            <i className="fas fa-users"></i>
          </div>
          <div className="metric-content">
            <h3>Active Subscriptions</h3>
            <div className="metric-value">{analytics.overview.activeSubscriptions}</div>
            <div className={`metric-change ${analytics.overview.subscriptionGrowth >= 0 ? 'positive' : 'negative'}`}>
              <i className={`fas fa-arrow-${analytics.overview.subscriptionGrowth >= 0 ? 'up' : 'down'}`}></i>
              {Math.abs(analytics.overview.subscriptionGrowth)}% vs last period
            </div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon users">
            <i className="fas fa-user-friends"></i>
          </div>
          <div className="metric-content">
            <h3>Total Users</h3>
            <div className="metric-value">{analytics.overview.totalUsers}</div>
            <div className={`metric-change ${analytics.overview.userGrowth >= 0 ? 'positive' : 'negative'}`}>
              <i className={`fas fa-arrow-${analytics.overview.userGrowth >= 0 ? 'up' : 'down'}`}></i>
              {Math.abs(analytics.overview.userGrowth)}% vs last period
            </div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon session">
            <i className="fas fa-clock"></i>
          </div>
          <div className="metric-content">
            <h3>Avg Session Duration</h3>
            <div className="metric-value">{analytics.overview.avgSessionDuration}</div>
            <div className={`metric-change ${analytics.overview.sessionGrowth >= 0 ? 'positive' : 'negative'}`}>
              <i className={`fas fa-arrow-${analytics.overview.sessionGrowth >= 0 ? 'up' : 'down'}`}></i>
              {Math.abs(analytics.overview.sessionGrowth)}% vs last period
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-grid">
        <div className="chart-card">
          <h3>User Activity Trend</h3>
          <div className="chart-placeholder">
            <div className="bar-chart">
              {analytics.userActivity.dailyActiveUsers.map((value, index) => (
                <div key={index} className="bar-container">
                  <div 
                    className="bar" 
                    style={{ height: `${(value / Math.max(...analytics.userActivity.dailyActiveUsers)) * 100}%` }}
                  >
                    <span className="bar-value">{value}</span>
                  </div>
                  <span className="bar-label">{analytics.userActivity.labels[index]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="chart-card">
          <h3>Revenue Growth</h3>
          <div className="chart-placeholder">
            <div className="line-chart">
              {analytics.revenue.monthly.map((value, index) => (
                <div key={index} className="line-point">
                  <div 
                    className="point" 
                    style={{ bottom: `${(value / Math.max(...analytics.revenue.monthly)) * 80}%` }}
                  >
                    <span className="point-value">${(value / 1000).toFixed(1)}k</span>
                  </div>
                  <span className="point-label">{analytics.revenue.labels[index]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Top Organizations */}
      <div className="section-card">
        <h3>Top Organizations by Revenue</h3>
        <div className="organizations-list">
          {analytics.topOrganizations.map((org, index) => (
            <div key={index} className="org-item">
              <div className="org-rank">{index + 1}</div>
              <div className="org-info">
                <div className="org-name">{org.name}</div>
                <div className="org-stats">
                  <span><i className="fas fa-users"></i> {org.users} users</span>
                  <span><i className="fas fa-dollar-sign"></i> ${org.revenue.toLocaleString()}</span>
                </div>
              </div>
              <div className={`org-growth ${org.growth >= 0 ? 'positive' : 'negative'}`}>
                <i className={`fas fa-arrow-${org.growth >= 0 ? 'up' : 'down'}`}></i>
                {org.growth}%
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Feature Usage */}
      <div className="section-card">
        <h3>Feature Usage Statistics</h3>
        <div className="features-list">
          {analytics.featureUsage.map((feature, index) => (
            <div key={index} className="feature-item">
              <div className="feature-name">{feature.feature}</div>
              <div className="feature-bar-container">
                <div className="feature-bar" style={{ width: `${feature.usage}%` }}>
                  <span className="feature-percentage">{feature.usage}%</span>
                </div>
              </div>
              <div className={`feature-trend ${feature.trend}`}>
                <i className={`fas fa-${feature.trend === 'up' ? 'arrow-up' : feature.trend === 'down' ? 'arrow-down' : 'minus'}`}></i>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;


